import { usePage } from "@inertiajs/react";
import { useEffect, useState } from "react";

import { FormField, RecordForm } from "@/components/forms/base-record-form";
import { useRecordForm } from "@/hooks/use-record-form";
import { similar_companies_path } from "@/routes";

interface CompanyFormProps {
  onCancel: () => void;
  path: string;
  onSuccess?: (company: { id: string; name: string; uid: string }) => void;
}

export function CompanyForm({ onCancel, path, onSuccess }: CompanyFormProps) {
  const account = usePage().props.current_account as { uid: string } | undefined;
  const [similar, setSimilar] = useState<{ uid: string; name: string }[]>([]);

  const { data, setField, errors, isSubmitting, submit } = useRecordForm({
    path,
    param: "company",
    initial: { name: "" },
    onSuccess: (record) =>
      onSuccess?.(record as { id: string; name: string; uid: string }),
  });

  // Did-you-mean (INCO-284): as the broker types, surface close existing
  // names so "Trafigura Pte Ltd" doesn't get a second life as "Trafigura".
  useEffect(() => {
    const name = data.name.trim();
    if (!account || name.length < 3) {
      setSimilar([]);
      return;
    }
    const timeout = setTimeout(() => {
      fetch(similar_companies_path(account.uid, { name }), {
        headers: { Accept: "application/json" },
      })
        .then((res) => (res.ok ? res.json() : { companies: [] }))
        .then((body: { companies: { uid: string; name: string }[] }) =>
          setSimilar(body.companies),
        )
        .catch(() => setSimilar([]));
    }, 250);
    return () => clearTimeout(timeout);
  }, [account, data.name]);

  return (
    <RecordForm
      onSubmit={submit}
      onCancel={onCancel}
      isSubmitting={isSubmitting}
      submitLabel="Create company"
    >
      <FormField
        label="Company name"
        name="name"
        required
        autoFocus
        value={data.name}
        onChange={(value) => setField("name", value)}
        errors={errors}
        hint={
          similar.length > 0
            ? `Similar existing: ${similar.map((c) => c.name).join(", ")}`
            : undefined
        }
      />
    </RecordForm>
  );
}
